/**
 * Views/Prenomina/SobreRecibo.jsx
 *
 * Modal de sobre-recibo — vista de balance general de un empleado.
 *
 * Estructura de columnas en el balance:
 *   Percepciones (izquierda):  Nombre | Valor (cantidad) | Importe
 *   Deducciones+Oblig (derecha): Nombre | Importe
 *
 * Solo las percepciones tienen Valor (cantidad ingresada por el usuario).
 * Deducciones y obligaciones solo tienen Importe.
 *
 * Tabs: Conceptos · Obligaciones · Acumulados · Días e Incidencias · INFONAVIT ·
 * Vacaciones · Movtos. Permanentes · Incid. Permanentes · FONACOT · Incapacidades ·
 * Datos Empleado.
 *
 * ── Memoria de cálculo ───────────────────────────────────────────────────────
 *
 * Doble clic sobre el NOMBRE de cualquier concepto abre ModalDetalleConcepto: los
 * cinco importes del concepto y, por cada uno, cómo funcionó su fórmula (variables
 * con su valor, la fórmula reescrita con números y el paso a paso).
 *
 * Los dos botones de arriba abren ModalDesgloseFiscal con la memoria de cálculo del
 * ISR y del IMSS, que es lo que las empresas cuadran con su contador.
 *
 * Los tres salen de UNA sola petición a `prenomina/desgloseLinea`, que vuelve a correr
 * el motor en modo simulación para este empleado. Por eso el resultado se cachea en el
 * estado y se tira en `componentDidUpdate` en cuanto la línea cambia.
 *
 * El doble clic va en el `<td>` del nombre y NO en el `<tr>`: cada renglón de la tabla
 * de balance contiene DOS conceptos distintos, una percepción y una deducción pareadas
 * por índice.
 */

import React, { Component } from 'react'
import axios from 'axios'
import { Tabs, Row, Col, Tag, Tooltip, Popconfirm, InputNumber } from 'antd'
import { ModalItem, ButtonItem } from '../../../../Components/Items'
import { ReloadOutlined, DeleteOutlined, CalculatorOutlined, UndoOutlined, FileSearchOutlined } from '@ant-design/icons'
import IncidenciasTab from '../IncidenciasTab/IncidenciasTab'
import VacacionesEmpleado from '../VacacionesEmpleado/VacacionesEmpleado'
import MovimientosPermanentesEmpleado from '../MovimientosPermanentesEmpleado/MovimientosPermanentesEmpleado'
import IncidenciasPermanentesEmpleado from '../IncidenciasPermanentesEmpleado/IncidenciasPermanentesEmpleado'
import InfonvitEmpleado from '../InfonvitEmpleado/InfonvitEmpleado'
import FonacotEmpleado from '../FonacotEmpleado/FonacotEmpleado'
import IncapacidadesEmpleado from '../IncapacidadesEmpleado/IncapacidadesEmpleado'
import ModalAcumulados from './ModalAcumulados'
import ModalDetalleConcepto from './ModalDetalleConcepto'
import ModalDesgloseFiscal from './ModalDesgloseFiscal'
// La celda y la tabla de conceptos viven aparte porque las comparte el desglose del finiquito:
// son dos pantallas que hacen lo mismo y tienen que verse iguales.
import CeldaEditable from './CeldaEditable'
import TablaConceptosRecibo from './TablaConceptosRecibo'

const { TabPane } = Tabs

// ─── Estilos ─────────────────────────────────────────────────────────────────
const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  color: '#555', whiteSpace: 'nowrap', textAlign: 'center',
}
const TD     = { border: '1px solid #d0d0d0', padding: '4px 8px', fontSize: 12, verticalAlign: 'middle' }
const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace' }

const fmt = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00'

// ─────────────────────────────────────────────────────────────────────────────
class SobreRecibo extends Component {

  state = {
    tabActiva: '1', vacacionesTabVista: false, movtosPermanentesTabVista: false, incidPermanentesTabVista: false, infonavitTabVista: false, fonacotTabVista: false, incapacidadesTabVista: false, modalAcumulados: false,
    // El borrador de la fila para agregar un concepto vive dentro de TablaConceptosRecibo.
    netoDeseado: null,
    calculandoNeto: false,
    deshaciendoNeto: false,

    // ── Memoria de cálculo ──────────────────────────────────────────────────
    // Una sola petición trae el desglose del concepto pedido, el del ISR y el del
    // IMSS, porque los tres salen del mismo recálculo. Se guardan aquí para que el
    // segundo y el tercer modal abran sin volver a pedir nada.
    desgloseISR: null,
    desgloseIMSS: null,
    avisoRecalculo: null,
    estatusLineaDesglose: null,
    conceptoDetalle: null,
    desgloseCargando: false,
    desgloseError: null,
    modalConcepto: false,
    modalFiscal: null,      // 'ISR' | 'IMSS' | null
  }

  // El desglose explica UNA foto de la línea. En cuanto la línea cambia —se editó un
  // importe, se recalculó, se agregó o quitó un concepto— la foto queda vieja y hay
  // que tirarla: si no, el modal explicaría números que ya no están en la pantalla.
  componentDidUpdate(prevProps) {
    if (prevProps.linea !== this.props.linea) {
      this.setState({
        desgloseISR: null, desgloseIMSS: null, avisoRecalculo: null,
        estatusLineaDesglose: null, conceptoDetalle: null, desgloseError: null,
      })
    }
  }

  /**
   * Pide la memoria de cálculo de la línea. El backend vuelve a correr el motor en
   * modo simulación para este empleado (no escribe nada) y devuelve todo junto.
   *
   * @param conceptoNominaId  concepto del que se quiere la traza completa de fórmulas,
   *                          o null cuando sólo se van a ver el ISR y el IMSS.
   * @param forzar            ignora la caché. Lo usa el botón de reintentar: si no,
   *                          un error con datos viejos en memoria dejaría el aviso de
   *                          error pegado sin forma de quitarlo.
   */
  async cargarDesglose(conceptoNominaId = null, forzar = false) {
    const { linea, prenomina, empresaId, headersToken } = this.props

    // Si ya se tiene el fiscal y no se pide un concepto nuevo, no hay nada que traer.
    if (!forzar && !conceptoNominaId && this.state.desgloseISR) return

    this.setState({ desgloseCargando: true, desgloseError: null })
    try {
      const res = await axios.post(
        'prenomina/desgloseLinea',
        {
          empresaId,
          prenominaId: prenomina?._id,
          empleadoId: linea.empleadoId?.toString(),
          conceptoNominaId,
        },
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        this.setState({
          desgloseCargando: false,
          desgloseISR:  res.data.isr,
          desgloseIMSS: res.data.imss,
          avisoRecalculo: res.data.avisoRecalculo,
          estatusLineaDesglose: res.data.estatusLinea,
          ...(conceptoNominaId ? { conceptoDetalle: res.data.concepto } : {}),
        })
      } else {
        this.setState({ desgloseCargando: false, desgloseError: res.data.message || 'No se pudo obtener el desglose' })
      }
    } catch (e) {
      this.setState({ desgloseCargando: false, desgloseError: 'No se pudo conectar con el servidor' })
    }
  }

  abrirDetalleConcepto(conceptoNominaId) {
    this.setState({ modalConcepto: true, conceptoDetalle: null })
    this.cargarDesglose(conceptoNominaId)
  }

  abrirDesgloseFiscal(tipo) {
    this.setState({ modalFiscal: tipo })
    this.cargarDesglose(null)
  }

  getNoSumaSet() {
    return new Set(
      (this.props.conceptosNomina || [])
        .filter(c => c.noSuma)
        .map(c => c._id?.toString())
    )
  }

  // Percepciones en especie: se ven con su importe y marcadas, pero fuera del neto. No van
  // en el set de noSuma porque no son informativas — éstas sí se timbran y sí gravan.
  getEspecieSet() {
    return new Set(
      (this.props.conceptosNomina || [])
        .filter(c => c.tipo === 1 && c.especie && !c.noSuma)
        .map(c => c._id?.toString())
    )
  }

  // ── Celdas de deducción/obligación: Nombre | Importe ─────────────────────
  renderCeldasDeduccion(c, noSuma = false) {
    const { onEditarImporte } = this.props
    if (!c) {
      return [
        <td key="n" style={{ ...TD, color: '#e0e0e0' }}>—</td>,
        <td key="i" style={{ ...TD_NUM, width: 90 }}></td>,
      ]
    }

    const esManualImporte = c.importeTotalManual !== null && c.importeTotalManual !== undefined
    const importeEfectivo = esManualImporte ? c.importeTotalManual : (c.importeTotal || 0)
    const tipoLabel = c.tipo === 3 ? 'OBL' : null

    const tdGris = noSuma ? { color: '#bbb' } : {}

    return [
      // Nombre — doble clic abre el detalle del cálculo del concepto
      <td
        key="n"
        style={{ ...TD, ...tdGris, cursor: 'pointer' }}
        onDoubleClick={() => this.abrirDetalleConcepto(c.conceptoNominaId.toString())}
      >
        <Tooltip title="Doble clic para ver cómo se calculó" mouseEnterDelay={0.6}>
          <span>
            <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#ccc', marginRight: 5 }}>
              {c.serie}-{c.numero}
            </span>
            {c.descripcion}
          </span>
        </Tooltip>
        {tipoLabel && <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>{tipoLabel}</Tag>}
        {noSuma && (
          <Tag color="default" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px', color: '#bbb' }}>Info</Tag>
        )}
        {esManualImporte && (
          this.props.soloLectura ? (
            <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>M</Tag>
          ) : (
            <Popconfirm
              title="¿Quitar el valor manual de este concepto? Quedará en 0 hasta que recalcules la línea."
              okText="Quitar" cancelText="Cancelar"
              onConfirm={() => this.props.onLimpiarManualConcepto(c.conceptoNominaId.toString())}
            >
              <Tag
                color="blue"
                style={{ marginLeft: 4, fontSize: 10, padding: '0 4px', cursor: 'pointer' }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                M ✕
              </Tag>
            </Popconfirm>
          )
        )}
        {c.agregadoManual && !this.props.soloLectura && (
          <Popconfirm
            title="¿Quitar esta deducción manual?"
            okText="Quitar" cancelText="Cancelar"
            onConfirm={() => this.props.onQuitarConcepto(c.conceptoNominaId.toString())}
          >
            <DeleteOutlined
              style={{ marginLeft: 6, color: '#ff4d4f', cursor: 'pointer' }}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        )}
      </td>,
      // Importe (sin columna Valor)
      <td key="i" style={{ ...TD_NUM, width: 90, ...tdGris }}>
        <CeldaEditable
          key={`i-${c.conceptoNominaId}-${importeEfectivo}`}
          valor={importeEfectivo}
          esManual={esManualImporte}
          ancho={80}
          disabled={this.props.soloLectura}
          onGuardar={(nv) =>
            onEditarImporte(c.conceptoNominaId.toString(), 'importeTotalManual', nv)
          }
        />
      </td>,
    ]
  }

  // ── Tab 1: Balance (percepciones y deducciones solo) ─────────────────────
  async confirmarNetoInverso() {
    const { netoDeseado } = this.state
    const netoNum = parseFloat(netoDeseado)
    if (isNaN(netoNum)) return
    this.setState({ calculandoNeto: true })
    const ok = await this.props.onCalcularNetoInverso(netoNum)
    this.setState({ calculandoNeto: false, ...(ok ? { netoDeseado: null } : {}) })
  }

  async confirmarDeshacerNetoInverso() {
    this.setState({ deshaciendoNeto: true })
    await this.props.onRevertirNetoInverso()
    this.setState({ deshaciendoNeto: false })
  }

  renderTabConceptos() {
    const { linea, onRecalcularLinea, soloLectura } = this.props
    const { netoDeseado, calculandoNeto, deshaciendoNeto } = this.state

    const noSumaIds   = this.getNoSumaSet()
    const percepciones = linea.conceptos?.filter(c => c.tipo === 1) || []
    const deducciones  = linea.conceptos?.filter(c => c.tipo === 2) || []
    const tieneNetoInverso = linea.salarioDiarioAntesNetoInverso !== null && linea.salarioDiarioAntesNetoInverso !== undefined

    // Los totales, el pareado de renglones y el neto los calcula TablaConceptosRecibo, que es
    // quien los pinta: tenerlos también aquí era la forma más fácil de que se separaran.

    return (
      <>
        {!soloLectura && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Neto deseado:</span>
              <InputNumber
                size="small"
                style={{ width: 110 }}
                value={netoDeseado}
                disabled={calculandoNeto}
                onChange={(v) => this.setState({ netoDeseado: v })}
                placeholder="$0.00"
              />
              <ButtonItem
                size="small"
                icon={<CalculatorOutlined />}
                loading={calculandoNeto}
                disabled={deshaciendoNeto || netoDeseado === null || netoDeseado === undefined || netoDeseado === ''}
                onClick={() => this.confirmarNetoInverso()}
              >
                Calcular
              </ButtonItem>
              {tieneNetoInverso && (
                <Popconfirm
                  title="¿Deshacer el cálculo de neto inverso? Se recalculará la línea."
                  okText="Deshacer"
                  cancelText="Cancelar"
                  onConfirm={() => this.confirmarDeshacerNetoInverso()}
                >
                  <ButtonItem
                    size="small"
                    icon={<UndoOutlined />}
                    loading={deshaciendoNeto}
                    disabled={calculandoNeto}
                  >
                    Deshacer cálculo inverso
                  </ButtonItem>
                </Popconfirm>
              )}
            </div>
            <Tooltip title="Recalcula respetando los valores manuales (azul)">
              <ButtonItem size="small" icon={<ReloadOutlined />} onClick={onRecalcularLinea}>
                Recalcular línea
              </ButtonItem>
            </Tooltip>
          </div>
        )}

        {/* La tabla vive en TablaConceptosRecibo.js, compartida con el desglose del finiquito:
            son dos pantallas que hacen lo mismo y tienen que verse iguales. */}
        <TablaConceptosRecibo
          percepciones={percepciones}
          deducciones={deducciones}
          noSumaIds={noSumaIds}
          especieIds={this.getEspecieSet()}
          soloLectura={soloLectura}
          empresaId={this.props.empresaId}
          headersToken={this.props.headersToken}
          onEditarImporte={this.props.onEditarImporte}
          onLimpiarManual={this.props.onLimpiarManualConcepto}
          onAgregar={(id, importe) => this.props.onAgregarConcepto(id, importe)}
          // Un concepto calculado no se borra, sólo se le puede forzar el importe a 0; los que se
          // agregaron a mano sí.
          puedeQuitar={(c) => !!c.agregadoManual}
          onQuitar={(c) => this.props.onQuitarConcepto(c.conceptoNominaId.toString())}
          tituloQuitar={(c) => (c.tipo === 1 ? '¿Quitar esta percepción manual?' : '¿Quitar esta deducción manual?')}
          onDetalleConcepto={(id) => this.abrirDetalleConcepto(id)}
          mensajeVacio={'Sin conceptos — presiona "Calcular todo" primero'}
          nota={(
            <>
              <b>Valor</b> = cantidad (ej: 6 días, 2 bonos). Solo aplica a percepciones.
              Al cambiar el Valor, la línea se recalcula sola (importe, ISR, IMSS y neto); en Sueldo y
              Séptimo día el importe queda en días × salario diario.
              Los campos en <span style={{ color: '#1677ff', fontWeight: 600 }}>azul (M)</span> son manuales.
              Los conceptos <Tag color="default" style={{ fontSize: 10, padding: '0 4px' }}>Info</Tag> son informativos y no suman al neto.
              Si capturas un importe a mano, presiona "Recalcular línea" para actualizar fórmulas respetando esos valores.
            </>
          )}
        />
      </>
    )
  }

  // ── Tab Obligaciones ──────────────────────────────────────────────────────
  renderTabObligaciones() {
    const { linea } = this.props
    const noSumaIds   = this.getNoSumaSet()
    const obligaciones = linea.conceptos?.filter(c => c.tipo === 3) || []

    const importeEfectivo = (c) => {
      const esManual = c.importeTotalManual !== null && c.importeTotalManual !== undefined
      return esManual ? c.importeTotalManual : (c.importeTotal || 0)
    }
    const totalOblSuma = obligaciones
      .filter(c => !noSumaIds.has(c.conceptoNominaId?.toString()))
      .reduce((s, c) => s + importeEfectivo(c), 0)

    return (
      <>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
          Las obligaciones son costos patronales (IMSS, SAR, INFONAVIT, etc.) y se usan para el cálculo de acumulados. No aparecen en el recibo del empleado.
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ ...TH, background: '#E6F1FB' }} colSpan={2}>OBLIGACIONES PATRONALES</th>
              </tr>
              <tr>
                <th style={{ ...TH, textAlign: 'left', minWidth: 260 }}>Concepto</th>
                <th style={{ ...TH, width: 110 }}>Importe</th>
              </tr>
            </thead>

            <tbody>
              {obligaciones.map((c, i) => (
                <tr key={i}>
                  {this.renderCeldasDeduccion(c, noSumaIds.has(c.conceptoNominaId?.toString()))}
                </tr>
              ))}
              {obligaciones.length === 0 && (
                <tr>
                  <td colSpan={2} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}>
                    Sin obligaciones calculadas
                  </td>
                </tr>
              )}
            </tbody>

            {obligaciones.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f0f0f0', fontWeight: 700 }}>
                  <td style={{ ...TD, fontWeight: 700 }}>TOTAL OBLIGACIONES</td>
                  <td style={{ ...TD_NUM, color: '#185FA5', background: '#E6F1FB' }}>
                    {fmt(totalOblSuma)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </>
    )
  }

  renderPlaceholder(titulo, descripcion) {
    return (
      <div style={{
        textAlign: 'center', padding: '48px 0', color: '#aaa',
        border: '2px dashed #e8e8e8', borderRadius: 8,
      }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🚧</div>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#555' }}>{titulo}</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>{descripcion}</div>
      </div>
    )
  }

  renderTabDatos() {
    const { linea } = this.props
    return (
      <Row gutter={[16, 10]} style={{ fontSize: 13, padding: '8px 0' }}>
        <Col span={12}><b>RFC:</b> {linea.rfc}</Col>
        <Col span={12}><b>NSS:</b> {linea.numSeguridadSocial}</Col>
        <Col span={12}><b>Salario Diario:</b> ${fmt(linea.salarioDiario)}</Col>
        <Col span={12}><b>SBC Topado:</b> ${fmt(linea.sbcTopado25UMAS || linea.sbcParteFija)}</Col>
        <Col span={12}><b>SBC Fija:</b> ${fmt(linea.sbcParteFija)}</Col>
        <Col span={12}><b>SBC Variable:</b> ${fmt(linea.sbcParteVariable)}</Col>
        <Col span={12}>
          <b>Días trabajados:</b>{' '}
          {linea.diasTrabajadosManual !== null && linea.diasTrabajadosManual !== undefined
            ? <><span style={{ color: '#1677ff', fontWeight: 600 }}>{linea.diasTrabajadosManual}</span>{' '}
                <Tag color="blue" style={{ fontSize: 10 }}>Manual</Tag></>
            : linea.diasTrabajados}
        </Col>
        <Col span={12}><b>Cálculo PTU:</b> {linea.calculoPTU ? 'Sí' : 'No'}</Col>
        <Col span={12}><b>Cálculo Aguinaldo:</b> {linea.calculoAguinaldo ? 'Sí' : 'No'}</Col>
        <Col span={12}><b>Cuenta:</b> {linea.numCuenta || '—'}</Col>
        <Col span={24}><b>CLABE:</b> {linea.clabeInterbancaria || '—'}</Col>
      </Row>
    )
  }

  render() {
    const { visible, onClose, linea, prenomina, onGuardarIncidenciaDia, tiposIncidencias, soloLectura, incidenciasSoloLectura } = this.props
    const { tabActiva, vacacionesTabVista, movtosPermanentesTabVista, incidPermanentesTabVista, infonavitTabVista, fonacotTabVista, incapacidadesTabVista } = this.state

    // Recalcular totales excluyendo conceptos noSuma y percepciones en especie
    const noSumaIds = this.getNoSumaSet()
    const especieIds = this.getEspecieSet()
    const importeEfectivo = (c) => {
      const esManual = c.importeTotalManual !== null && c.importeTotalManual !== undefined
      return esManual ? c.importeTotalManual : (c.importeTotal || 0)
    }
    // En efectivo: lo que sí se le deposita. Ver TablaConceptosRecibo.
    const totalPercSuma = (linea.conceptos || [])
      .filter(c => c.tipo === 1 && !noSumaIds.has(c.conceptoNominaId?.toString()))
      .filter(c => !especieIds.has(c.conceptoNominaId?.toString()))
      .reduce((s, c) => s + importeEfectivo(c), 0)
    const totalDeduSuma = (linea.conceptos || [])
      .filter(c => c.tipo === 2 && !noSumaIds.has(c.conceptoNominaId?.toString()))
      .reduce((s, c) => s + importeEfectivo(c), 0)
    const totalOblSuma = (linea.conceptos || [])
      .filter(c => c.tipo === 3 && !noSumaIds.has(c.conceptoNominaId?.toString()))
      .reduce((s, c) => s + importeEfectivo(c), 0)

    return (
      <>
      <ModalItem
        title={
          <span>
            Sobre-recibo —{' '}
            <b>{linea.apellidoPaterno} {linea.apellidoMaterno} {linea.nombre}</b>
            {linea.tieneModificaciones && (
              <Tag color="blue" style={{ marginLeft: 8 }}>Con manuales</Tag>
            )}
          </span>
        }
        visible={visible}
        spinning={false}
        onCancel={onClose}
        footer={null}
        width={1060}
        destroyOnClose={false}
      >
        {/* Banner solo lectura */}
        {soloLectura && (
          <div style={{ marginBottom: 12, padding: '7px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 12, color: '#7c5a00' }}>
            Nómina timbrada — visualización de solo lectura. Los importes no se pueden modificar.
          </div>
        )}

        {/* Tarjetas resumen */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          {[
            { label: 'Percepciones', val: totalPercSuma,           bg: '#EAF3DE', color: '#3B6D11' },
            { label: 'Deducciones',  val: totalDeduSuma,           bg: '#FCEBEB', color: '#A32D2D' },
            { label: 'Obligaciones', val: totalOblSuma,            bg: '#E6F1FB', color: '#185FA5' },
            { label: 'Neto a pagar', val: totalPercSuma - totalDeduSuma, bg: '#f5f5f5', color: '#222' },
          ].map(({ label, val, bg, color }) => (
            <Col span={6} key={label}>
              <div style={{ background: bg, borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt(val)}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Memoria de cálculo fiscal. Va aquí arriba, fuera de los tabs, porque es lo
            que se pide desde cualquier parte del sobre-recibo y son los dos números
            que las empresas cuadran contra su contador. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #f0f0f0',
        }}>
          <span style={{ fontSize: 11, color: '#999' }}>
            Doble clic en cualquier concepto para ver cómo se calculó.
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <ButtonItem
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => this.abrirDesgloseFiscal('ISR')}
            >
              ¿Cómo se calculó el ISR?
            </ButtonItem>
            <ButtonItem
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => this.abrirDesgloseFiscal('IMSS')}
            >
              ¿Cómo se calculó el IMSS?
            </ButtonItem>
          </div>
        </div>

        <Tabs
          activeKey={tabActiva}
          onChange={k => this.setState({
            tabActiva: k,
            vacacionesTabVista:        k === '6' ? true : vacacionesTabVista,
            movtosPermanentesTabVista: k === '7' ? true : movtosPermanentesTabVista,
            incidPermanentesTabVista:  k === '10' ? true : incidPermanentesTabVista,
            infonavitTabVista:         k === '4' ? true : infonavitTabVista,
            fonacotTabVista:           k === '8' ? true : fonacotTabVista,
            incapacidadesTabVista:     k === '9' ? true : incapacidadesTabVista,
          })}
        >
          <TabPane tab="Conceptos" key="1">
            {this.renderTabConceptos()}
          </TabPane>
          <TabPane tab="Obligaciones" key="2b">
            {this.renderTabObligaciones()}
          </TabPane>
          <TabPane tab="Acumulados" key="2c">
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                Acumulados del ejercicio — importes mensuales por tipo de acumulado.
              </div>
              <ButtonItem
                type="primary"
                onClick={() => this.setState({ modalAcumulados: true })}
              >
                Ver Acumulados del Ejercicio
              </ButtonItem>
            </div>
          </TabPane>
          <TabPane tab="Días e Incidencias" key="2">
            <IncidenciasTab
              linea={linea}
              prenomina={prenomina}
              tiposIncidencias={tiposIncidencias}
              onGuardarDia={onGuardarIncidenciaDia}
              // Recibo timbrado/histórico o nómina cerrada (soloLectura), o la empresa configuró
              // las incidencias sólo para consulta. Antes no se le pasaba nada y la rejilla
              // dejaba capturar algo que el servidor rechazaba al guardar.
              soloLectura={soloLectura || incidenciasSoloLectura}
            />
          </TabPane>

          <TabPane tab="INFONAVIT" key="4">
            {infonavitTabVista && (
              <InfonvitEmpleado
                empleadoId={linea.empleadoId}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="Vacaciones" key="6">
            {vacacionesTabVista && (
              <VacacionesEmpleado
                empleadoId={linea.empleadoId}
                periodoId={prenomina?._id}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="Movtos. Permanentes" key="7">
            {movtosPermanentesTabVista && (
              <MovimientosPermanentesEmpleado
                empleadoId={linea.empleadoId}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="Incid. Permanentes" key="10">
            {incidPermanentesTabVista && (
              <IncidenciasPermanentesEmpleado
                empleadoId={linea.empleadoId}
                prenomina={prenomina}
                tiposIncidencias={tiposIncidencias}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="FONACOT" key="8">
            {fonacotTabVista && (
              <FonacotEmpleado
                empleadoId={linea.empleadoId}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="Incapacidades" key="9">
            {incapacidadesTabVista && (
              <IncapacidadesEmpleado
                empleadoId={linea.empleadoId}
                periodoId={prenomina?._id}
                salarioDiario={linea.salarioDiario}
                readOnly={soloLectura}
                timbrado={soloLectura}
              />
            )}
          </TabPane>
          <TabPane tab="Datos Empleado" key="5">
            {this.renderTabDatos()}
          </TabPane>
        </Tabs>
      </ModalItem>

      <ModalAcumulados
        empleadoId={linea.empleadoId?.toString()}
        empresaId={prenomina?.empresaId?.toString()}
        anio={prenomina?.anio || new Date().getFullYear()}
        open={this.state.modalAcumulados}
        onClose={() => this.setState({ modalAcumulados: false })}
        readOnly={soloLectura}
        headersToken={this.props.headersToken}
      />

      {this.state.modalConcepto && (
        <ModalDetalleConcepto
          open
          onClose={() => this.setState({ modalConcepto: false })}
          concepto={this.state.conceptoDetalle}
          loading={this.state.desgloseCargando}
          error={this.state.desgloseError}
          onReintentar={() => this.cargarDesglose(this.state.conceptoDetalle?.conceptoNominaId || null, true)}
          avisoRecalculo={this.state.avisoRecalculo}
          estatusLinea={this.state.estatusLineaDesglose}
        />
      )}

      {this.state.modalFiscal && (
        <ModalDesgloseFiscal
          open
          tipo={this.state.modalFiscal}
          onClose={() => this.setState({ modalFiscal: null })}
          desglose={this.state.modalFiscal === 'ISR' ? this.state.desgloseISR : this.state.desgloseIMSS}
          loading={this.state.desgloseCargando}
          // Si ya hay un desglose en memoria, un error de una petición posterior (por
          // ejemplo la de otro concepto) no debe tapar lo que sí se puede mostrar.
          error={this.state.desgloseISR ? null : this.state.desgloseError}
          onReintentar={() => this.cargarDesglose(null, true)}
          linea={linea}
          prenomina={prenomina}
          empresaNombre={this.props.empresaNombre}
          avisoRecalculo={this.state.avisoRecalculo}
          estatusLinea={this.state.estatusLineaDesglose}
        />
      )}
      </>
    )
  }
}

export default SobreRecibo